// src/components/pages/mosque/MosqueSearchPage.tsx
import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../utils/i18nStub';

import MosqueFilters, { MosqueFilterState } from './MosqueFilters';
import { listMosques, mosqueTypeColors } from '../../../services/mosqueService'; // ✅ import your map
import { MosqueListItem } from '../../../types/mosque';
import { useNavigate } from "react-router-dom";

// ✅ Map popup (same component you use in lands)
import MapModal from '../../maps/MapModal';

import Breadcrumbs from "../../../components/navigation/Breadcrumbs";

// ------------------------------------------------------------
// helpers
// ------------------------------------------------------------
const toInt = (s: string | null, d: number): number => {
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : d;
};

// Normalize hasWomenPrayerRoom ("yes"/"no"/truthy) → boolean
const toBool = (v: unknown): boolean => {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'yes' || s === 'y' || s === 'true' || s === '1' || s === 't' || s === '✓' || s === 'نعم') return true;
  if (s === 'no' || s === 'n' || s === 'false' || s === '0' || s === 'f' || s === 'لا') return false;
  return !!v;
};

// ✅ map Arabic label → one of your keys: "جامع", "فروض", "العيد"
function getClassificationPillClass(raw?: string | null): string {
  if (!raw) return 'bg-gray-200 text-gray-800';
  const s = String(raw).trim();

  // Exact match first
  if (s === 'جامع') return mosqueTypeColors['جامع'];
  if (s === 'فروض') return mosqueTypeColors['فروض'];
  if (s === 'العيد') return mosqueTypeColors['العيد'];

  // Loose contains match (handles variants like "مسجد جامع", "مصلى العيد", etc.)
  const lower = s.toLowerCase();
  if (lower.includes('جامع')) return mosqueTypeColors['جامع'];
  if (lower.includes('فروض')) return mosqueTypeColors['فروض'];
  if (lower.includes('العيد') || lower.includes('مصلى') || lower.includes('مُصلى')) return mosqueTypeColors['العيد'];

  // Fallback
  return 'bg-gray-200 text-gray-800';
}

  
const MosqueSearchPage: React.FC = () => {
  const { t } = useTranslation('mosque');
  const [sp, setSp] = useSearchParams();

  // paging from URL
  const pageNo = toInt(sp.get('pageNo'), 1);
  const pageSize = toInt(sp.get('pageSize'), 12);
  const searchQuery = sp.get('search') || '';

  // initial filters from URL
  const initial: MosqueFilterState = useMemo(
    () => ({
      regionId: sp.get('regionId') ? Number(sp.get('regionId')) : null,
      cityId: sp.get('cityId') ? Number(sp.get('cityId')) : null,
      centerId: sp.get('centerId') ? Number(sp.get('centerId')) : null,
      classificationId: sp.get('classificationId') ? Number(sp.get('classificationId')) : null,
      mosqueTypeId: sp.get('mosqueTypeId') ? Number(sp.get('mosqueTypeId')) : null,
      hasWomenSection: sp.get('hasWomenSection') ? sp.get('hasWomenSection') === 'true' : null,
      // ✅ NEW
      mosqueName: sp.get('mosqueName') || null,
      mosqueNumber: sp.get('mosqueNumber') || null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sp.toString()]
  );

  // list state
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<MosqueListItem[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
const navigate = useNavigate();

  // ------------------------------------------------------------
  // Map modal state (same UX as lands)
  // ------------------------------------------------------------
  const [mapItem, setMapItem] = useState<MosqueListItem | null>(null);
  const openMap = (it: MosqueListItem) => setMapItem(it);
  const closeMap = () => setMapItem(null);

  // fetch function
  async function load() {
    setLoading(true);
    try {
      const res = await listMosques({
        regionId: initial.regionId ?? undefined,
        cityId: initial.cityId ?? undefined,
        centerId: initial.centerId ?? undefined,
        classificationId: initial.classificationId ?? undefined,
        mosqueTypeId: initial.mosqueTypeId ?? undefined,
        hasWomenSection: initial.hasWomenSection ?? undefined,
        keyword: searchQuery || undefined,
          // ✅ NEW: send new backend filters
        mosqueName: initial.mosqueName ?? undefined,
        mosqueNumber: initial.mosqueNumber ?? undefined,

        pageNo,
        pageSize,
      });
      setItems(res.items);
      setPageCount(res.pageCount);
      setTotalCount(res.totalCount);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.toString()]);

  // apply filters -> write to URL & reset page to 1
  function applyFilters(f: MosqueFilterState) {
    const next = new URLSearchParams(sp);
    const setOrDel = (k: string, v?: number | boolean | null) => {
      if (v == null || (typeof v === 'number' && Number.isNaN(v))) next.delete(k);
      else next.set(k, String(v));
    };

      const setOrDelText = (k: string, v?: string | null) => {
    const s = (v ?? "").trim();
    if (!s) next.delete(k);
    else next.set(k, s);
  };

    setOrDel('regionId', f.regionId);
    setOrDel('cityId', f.cityId);
    setOrDel('centerId', f.centerId);
    setOrDel('classificationId', f.classificationId);
    setOrDel('mosqueTypeId', f.mosqueTypeId);
    setOrDel('hasWomenSection', f.hasWomenSection);

      // ✅ NEW
    setOrDelText('mosqueName', f.mosqueName);
    setOrDelText('mosqueNumber', f.mosqueNumber);
    
    next.set('pageNo', '1');
    next.set('pageSize', String(pageSize));
    setSp(next, { replace: false });
  }

  // pager
  const go = (n: number) => {
    const next = new URLSearchParams(sp);
    next.set('pageNo', String(n));
    next.set('pageSize', String(pageSize));
    setSp(next, { replace: false });
    // keep: scroll to top like you requested earlier
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // page-size change -> reset to page 1 and persist in URL
  const changePageSize = (n: number) => {
    const next = new URLSearchParams(sp);
    next.set('pageNo', '1');
    next.set('pageSize', String(n));
    setSp(next, { replace: false });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
      <div className="space-y-6">
        <Breadcrumbs></Breadcrumbs>
       
        
        {/* Filters (unchanged API; same overlay layout as lands) */}
        <MosqueFilters onApply={applyFilters} initial={initial} />

      {/* result meta */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-neutral-500 text-sm">
          {t('search.totalResults', 'إجمالي النتائج')}: <strong className="text-neutral-800">{totalCount}</strong>
        </div>
        {loading && (
          <div className="text-neutral-500 text-sm flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" role="status" aria-label="loading">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
              <path className="opacity-75" d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" fill="none" />
            </svg>
            {t('search.loading', 'جاري التحميل...')}
          </div>
        )}
      </div>

      {/* Results */}
      {!loading && (
        <>
          {items.length > 0 ? (
            <>
              {/* Grid (same breakpoints as lands) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((mosque) => {
                  // ✅ Allow both shapes for women section
                  const m = mosque as MosqueListItem & { hasWomenPrayerRoom?: string | boolean | null; hasWomenSection?: string | boolean | null };
                  const womenRaw = m.hasWomenPrayerRoom ?? m.hasWomenSection;
                  const womenYes = toBool(womenRaw);

                  // ✅ Map pill color from your existing helper
                  const pillClass = mosque.mosqueClassification
                    ? getClassificationPillClass(mosque.mosqueClassification)
                    : "";

                  // put this near where canShowMap is defined
                  const latNum = mosque?.latitude == null ? NaN : Number(mosque.latitude);
                  const lngNum = mosque?.longitude == null ? NaN : Number(mosque.longitude);
                  const canShowMap = Number.isFinite(latNum) && Number.isFinite(lngNum);


                  const goDetails = () => {
                    navigate(`/mosques/${mosque.id}`, { state: { fromList: true } });
                  };

                  return (
                    <div key={mosque.id} className="h-full">
                      {/* Home Page Style Card */}
                      <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full relative">
                        {/* Top-right badge - Classification */}
                        {mosque.mosqueClassification && (
                          <div className="absolute top-4 end-4 z-10">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${pillClass}`}
                              title={mosque.mosqueClassification}
                            >
                              <i className="hgi-stroke hgi-star text-xs" />
                              {mosque.mosqueClassification}
                            </span>
                          </div>
                        )}

                        {/* Icon - Home Page Style */}
                        <div className="mb-4">
                          <img
                            src={`${process.env.PUBLIC_URL}/dga_v2/assets/img/moiaIcons/mosqueIcon1.svg`}
                            alt="أيقونة المسجد"
                            className="w-10 h-10"
                            width={40}
                            height={40}
                            loading="lazy"
                            draggable={false}
                            style={{ filter: 'brightness(0) saturate(100%) invert(25%) sepia(95%) saturate(1200%) hue-rotate(130deg) brightness(85%) contrast(95%)' }}
                          />
                        </div>

                        {/* Title - Home Page Style */}
                        <h3 className="text-xl font-bold text-gray-900 mb-3 text-right leading-tight line-clamp-2 min-h-[56px]">
                          <button 
                            className="hover:text-sa-600 transition-colors text-right" 
                            onClick={goDetails}
                          >
                            {mosque.name || t("unknown_mosque")}
                          </button>
                        </h3>

                        {/* Address */}
                        <div className="mb-4 text-right">
                          <div className="flex items-start gap-2 text-right">
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-gray-600 text-sm leading-relaxed flex-1">
                              {mosque.fullAddress || "—"}
                            </span>
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="space-y-2.5 flex-1 mb-4">
                          {/* 1. الرقم التسلسلي */}
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500 text-sm font-medium">{t("box.number", "الرقم التسلسلي")}</span>
                            <span className="text-gray-900 font-semibold text-sm">
                              {mosque.number || "—"}
                            </span>
                          </div>

                          {/* 2. المنطقة */}
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500 text-sm font-medium">{t("box.region", "المنطقة")}</span>
                            <span className="text-gray-900 font-semibold text-sm">
                              {mosque.regionName || "—"}
                            </span>
                          </div>

                          {/* 3. المدينة */}
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500 text-sm font-medium">{t("box.city", "المدينة")}</span>
                            <span className="text-gray-900 font-semibold text-sm">
                              {mosque.cityName || "—"}
                            </span>
                          </div>

                          {/* 4. المركز */}
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500 text-sm font-medium">{t("box.center", "المركز")}</span>
                            <span className="text-gray-900 font-semibold text-sm">
                              {mosque.centerName || "—"}
                            </span>
                          </div>

                          {/* 5. الحي */}
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500 text-sm font-medium">{t("box.district", "الحي")}</span>
                            <span className="text-gray-900 font-semibold text-sm">
                              {mosque.districtName || "—"}
                            </span>
                          </div>

                          {/* 6. قسم النساء */}
                          <div className="flex items-center justify-between py-2">
                            <span className="text-gray-500 text-sm font-medium">{t("box.women_section", "قسم النساء")}</span>
                            <span
                              className={
                                "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold " +
                                (womenYes
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-gray-50 text-gray-600 border border-gray-200")
                              }
                            >
                              {
                                womenRaw === "separate"
                                  ? t("filters.womenSections.separate", "منفصل")
                                  : womenRaw === "attached"
                                    ? t("filters.womenSections.attached", "ملحق")
                                    : womenYes
                                      ? t("filters.womenSections.yes", "نعم")
                                      : t("filters.womenSections.no", "لا")
                              }
                            </span>
                          </div>
                        </div>

                        {/* Footer with Buttons - Home Page Style */}
                        <div className="mt-auto pt-4 border-t border-gray-100">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={goDetails}
                              className="w-full h-11 flex items-center justify-center gap-2 rounded-lg bg-sa-600 text-white text-sm font-semibold hover:bg-sa-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span>{t("box.buttons.details", "عرض التفاصيل")}</span>
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openMap(mosque); // ALWAYS open modal; modal will show coords or fallback
                              }}
                              className={`w-full h-11 flex items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${
                                canShowMap
                                  ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400"
                                  : "border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-200"
                              }`}
                              title={canShowMap ? t("box.buttons.map", "استعراض الخريطة") : t("box.map_unavailable", "الخريطة غير متاحة")}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{t("box.buttons.map", "استعراض الخريطة")}</span>
                            </button>

                          </div>
                        </div>
                      </article>
                    </div>
                  );
                })}

              </div>

              {/* pager (same UX as lands) */}
              {pageCount > 1 && (
                <div className="flex items-center justify-center gap-3 pt-4">
                  <button
                    className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    disabled={pageNo <= 1}
                    onClick={() => go(pageNo - 1)}
                  >
                    {t('list.prev', 'السابق')}
                  </button>
                  <div className="text-neutral-500 text-sm">
                    {t('list.pageOf', 'صفحة {{page}} من {{total}}', { page: pageNo, total: pageCount })}
                  </div>
                  <button
                    className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    disabled={pageNo >= pageCount}
                    onClick={() => go(pageNo + 1)}
                  >
                    {t('list.next', 'التالي')}
                  </button>
                </div>
              )}

              {/* page-size dropdown (same as lands) */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <label className="text-sm text-neutral-500">
                  {t('list.pageSizeLabel', 'حجم الصفحة')}
                </label>
                <select
                  className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-sa-500"
                  style={{ width: 'auto' }}
                  value={pageSize}
                  onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                </select>
              </div>
            </>
          ) : (
            // No results
            <div className="text-center py-12">
              <div className="text-neutral-500">
                <img
                  src={`${process.env.PUBLIC_URL}/dga_v2/assets/img/moiaIcons/mosque.svg`}
                  alt=""                  // decorative
                  width={64}
                  height={64}
                  className="mx-auto mb-3 select-none"
                  aria-hidden="true"
                  draggable={false}
                />
                <p
                  className="font-arabic text-neutral-700"
                  style={{
                    fontFamily: 'var(--font-family-arabic)',
                    fontWeight: 'var(--font-weight-normal)',
                  }}
                >
                  {(searchQuery || Object.values(initial).some((f) => f !== null))
                    ? t('search.noResults', 'لم يتم العثور على مساجد تطابق معايير البحث.')
                    : t('search.startSearch', 'استخدم المرشحات أعلاه للبحث عن المساجد.')}
                </p>
              </div>
            </div>

          )}
        </>
      )}

      {/* Map modal (like lands) */}
      <MapModal
        open={!!mapItem}
        onClose={closeMap}
        lat={mapItem?.latitude ?? null}
        lng={mapItem?.longitude ?? null}
        title={mapItem?.fullAddress ?? (mapItem?.name ?? '')}
      />
      </div>
    </div>
  );
};

export default MosqueSearchPage;
